<div class="conflict-details-content">
  @if (variant === 'identity') {
    <div class="conflict-description">
      {{ conflictDescription }}
    </div>

    <div class="overview-panel">
      @if (conflicts.length > 0) {
        <div class="overview-badge">{{ conflicts.length }} possible {{ conflicts.length === 1 ? 'conflict path' : 'conflict paths' }}</div>
      }

      @if (selectedRoleAttributes.length > 0) {
        <div class="summary-block summary-card">
          <div class="summary-label">Selected Role Attributes</div>
          <ng-container
            *ngTemplateOutlet="roleAttributeList; context: { roles: selectedRoleAttributes }"
          ></ng-container>
        </div>
      }

      @if (conflicts.length > 0) {
        <div class="overview-text summary-card subtle">
          Review the matched bind and dial paths below to see which policies, services, and addresses may create the conflict.
        </div>
      }
    </div>

    @if (conflicts.length > 0) {
      <div class="section-header">
        <div class="section-title">Conflict Details</div>
        <div class="section-text">Each card shows one possible path that may allow unintended connectivity.</div>
      </div>
      <div class="conflicts-container">
        @for (conflict of conflicts; track conflict; let conflictIndex = $index) {
          <div class="conflict-card">
            <ng-container
              *ngTemplateOutlet="conflictCardHeader; context: { index: conflictIndex, name: identityName, roles: selectedRoleAttributes }"
            ></ng-container>

            <ng-container
              *ngTemplateOutlet="reasonPanel; context: { reason: getIdentityConflictReason(conflict), overlap: conflict?.overlap, signals: getIdentityConflictSignals(conflict) }"
            ></ng-container>

            <ng-container *ngTemplateOutlet="comparisonSections; context: { conflict: conflict }"></ng-container>
          </div>
        }
      </div>
    } @else {
      <div class="empty-state">
        {{ data?.emptyStateMessage || 'Detailed conflict information is not currently available.' }}
      </div>
    }
  } @else {
    <div class="conflict-description">
      {{ conflictDescription }}
    </div>

    <div class="overview-panel">
      @if (identities.length > 0) {
        <div class="overview-badge">
          {{ totalAffectedIdentityCount }} affected {{ totalAffectedIdentityCount === 1 ? 'identity' : 'identities' }}
        </div>
      }

      @if (data?.conflictDetails) {
        <div class="policy-summary-table-wrap summary-card">
          <table class="policy-summary-table">
            <thead>
              <tr>
                <th scope="col" class="summary-label">Identity Attributes</th>
                <th scope="col" class="summary-label">Service Attributes</th>
                <th scope="col" class="summary-label">Semantic</th>
                <th scope="col" class="summary-label">Policy Type</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  @if (policyIdentityAttributes.length > 0) {
                    <ng-container
                      *ngTemplateOutlet="roleAttributeList; context: { roles: policyIdentityAttributes }"
                    ></ng-container>
                  } @else {
                    <div class="summary-value">None</div>
                  }
                </td>
                <td>
                  @if (policyServiceAttributes.length > 0) {
                    <ng-container
                      *ngTemplateOutlet="roleAttributeList; context: { roles: policyServiceAttributes }"
                    ></ng-container>
                  } @else {
                    <div class="summary-value">None</div>
                  }
                </td>
                <td>
                  <div class="summary-value">{{ data?.conflictDetails?.semantic || data?.semantic || 'AnyOf' }}</div>
                </td>
                <td>
                  <div class="summary-value">{{ data?.conflictDetails?.policyType || data?.conflictDetails?.type || data?.policyType || 'Bind' }}</div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      }

      @if (identities.length > 0) {
        <div class="overview-text summary-card subtle">
          Start with the identities below, then review each conflict card to see which bind and dial policies overlap and where the address path may be created.
        </div>
      }

      @if (showLoadedIdentityCount) {
        <div class="overview-text summary-card subtle">
          Showing {{ identities.length }} of {{ totalAffectedIdentityCount }} affected identities. More identities load as you scroll.
        </div>
      }
    </div>

    @if (identities.length > 0) {
      <div class="section-header">
        <div class="section-title">Affected Identities</div>
        <div class="section-text">Each identity section shows the conflict paths that this policy may enable.</div>
      </div>
      @for (identityEntry of identities; track identityEntry) {
        <ng-container>
        @for (conflict of identityEntry?.conflicts || []; track conflict; let conflictIndex = $index) {
          <div class="conflict-card">
            <ng-container
              *ngTemplateOutlet="conflictCardHeader; context: { index: conflictIndex, name: identityEntry?.identity?.name || 'Unknown identity', roles: getIdentityEntryRoleAttributes(identityEntry) }"
            ></ng-container>

            <ng-container
              *ngTemplateOutlet="reasonPanel; context: { reason: getPolicyReason(identityEntry, conflict), overlap: conflict?.overlap, signals: getPolicySignals(identityEntry, conflict) }"
            ></ng-container>

            <ng-container *ngTemplateOutlet="comparisonSections; context: { conflict: conflict }"></ng-container>
          </div>
        }
        </ng-container>
      }
    } @else {
      <div class="empty-state">
        {{ data?.emptyStateMessage || 'Detailed conflict information is not currently available for this policy.' }}
      </div>
    }
  }
</div>

<ng-template #comparisonSections let-conflict="conflict">
  <div class="section-header compact">
    <div class="section-title">Service Comparison</div>
    <div class="section-text">Compare the services matched on the bind and dial sides.</div>
  </div>
  <div class="comparison-table">
    <div class="comparison-table-header comparison-table-label-cell"></div>
    <div class="comparison-table-header comparison-table-side bind">Dial Service</div>
    <div class="comparison-table-header comparison-table-side dial">Bind Service</div>

    <div class="comparison-table-label-cell">Service Name</div>
    <div class="comparison-table-value-cell">{{ conflict.dialService?.name || 'Unknown' }}</div>
    <div class="comparison-table-value-cell">{{ conflict.bindService?.name || 'Unknown' }}</div>

    <div class="comparison-table-label-cell">Role Attributes</div>
    <div class="comparison-table-value-cell chips">
      <ng-container
        *ngTemplateOutlet="roleAttributeList; context: { roles: getRoleList(conflict.dialService?.roleAttributes), inline: true, emptyClass: '' }"
      ></ng-container>
    </div>
    <div class="comparison-table-value-cell chips">
      <ng-container
        *ngTemplateOutlet="roleAttributeList; context: { roles: getRoleList(conflict.bindService?.roleAttributes), inline: true, emptyClass: '' }"
      ></ng-container>
    </div>
  </div>

  <div class="section-header compact">
    <div class="section-title">Service Policy Comparison</div>
    <div class="section-text">Compare the bind and dial policies that together create this conflict path.</div>
  </div>
  <div class="comparison-table">
    <div class="comparison-table-header comparison-table-label-cell"></div>
    <div class="comparison-table-header comparison-table-side bind">Dial Policy</div>
    <div class="comparison-table-header comparison-table-side dial">Bind Policy</div>

    <div class="comparison-table-label-cell">Policy Name</div>
    <div class="comparison-table-value-cell">{{ conflict.dialServicePolicy?.name || 'Unknown' }}</div>
    <div class="comparison-table-value-cell">{{ conflict.bindServicePolicy?.name || 'Unknown' }}</div>

    <div class="comparison-table-label-cell">Policy Type</div>
    <div class="comparison-table-value-cell">{{ conflict.dialServicePolicy?.policyType || 'Unknown' }}</div>
    <div class="comparison-table-value-cell">{{ conflict.bindServicePolicy?.policyType || 'Unknown' }}</div>

    <div class="comparison-table-label-cell">Semantic</div>
    <div class="comparison-table-value-cell">{{ conflict.dialServicePolicy?.semantic || 'Unknown' }}</div>
    <div class="comparison-table-value-cell">{{ conflict.bindServicePolicy?.semantic || 'Unknown' }}</div>

    <div class="comparison-table-label-cell">Identity Roles</div>
    <div class="comparison-table-value-cell chips">
      <ng-container
        *ngTemplateOutlet="roleAttributeList; context: { roles: getRoleList(conflict.dialServicePolicy?.identityRoles), inline: true, emptyClass: '' }"
      ></ng-container>
    </div>
    <div class="comparison-table-value-cell chips">
      <ng-container
        *ngTemplateOutlet="roleAttributeList; context: { roles: getRoleList(conflict.bindServicePolicy?.identityRoles), inline: true, emptyClass: '' }"
      ></ng-container>
    </div>

    <div class="comparison-table-label-cell">Service Roles</div>
    <div class="comparison-table-value-cell chips">
      <ng-container
        *ngTemplateOutlet="roleAttributeList; context: { roles: getRoleList(conflict.dialServicePolicy?.serviceRoles), inline: true, emptyClass: '' }"
      ></ng-container>
    </div>
    <div class="comparison-table-value-cell chips">
      <ng-container
        *ngTemplateOutlet="roleAttributeList; context: { roles: getRoleList(conflict.bindServicePolicy?.serviceRoles), inline: true, emptyClass: '' }"
      ></ng-container>
    </div>
  </div>

  <div class="section-header compact">
    <div class="section-title">Address Comparison</div>
    <div class="section-text">Compare the {{ variant === 'identity' ? 'ingress and egress sides' : 'two address sides' }} that may allow the conflicting path.</div>
  </div>
  <div class="comparison-table address-comparison-table">
    <div class="comparison-table-header comparison-table-label-cell"></div>
    <div class="comparison-table-header comparison-table-side bind">Ingress</div>
    <div class="comparison-table-header comparison-table-side dial">Egress</div>

    <div class="comparison-table-label-cell">Address Name</div>
    <div class="comparison-table-value-cell">{{ conflict.ingress?.name || 'Unknown' }}</div>
    <div class="comparison-table-value-cell">{{ conflict.egress?.name || 'Unknown' }}</div>

    <div class="comparison-table-label-cell">Config Type</div>
    <div class="comparison-table-value-cell">{{ getAddressConfigType(conflict.ingress) }}</div>
    <div class="comparison-table-value-cell">{{ getAddressConfigType(conflict.egress) }}</div>

    <div class="comparison-table-label-cell">Hosts</div>
    <div class="comparison-table-value-cell">{{ getAddressHosts(conflict.ingress) }}</div>
    <div class="comparison-table-value-cell">{{ getAddressHosts(conflict.egress) }}</div>

    <div class="comparison-table-label-cell">IPs</div>
    <div class="comparison-table-value-cell chips">
      <ng-container
        *ngTemplateOutlet="comparisonChipList; context: { items: getAddressIps(conflict.ingress) }"
      ></ng-container>
    </div>
    <div class="comparison-table-value-cell chips">
      <ng-container
        *ngTemplateOutlet="comparisonChipList; context: { items: getAddressIps(conflict.egress) }"
      ></ng-container>
    </div>

    <div class="comparison-table-label-cell">Ports</div>
    <div class="comparison-table-value-cell">{{ getAddressPorts(conflict.ingress) }}</div>
    <div class="comparison-table-value-cell">{{ getAddressPorts(conflict.egress) }}</div>
  </div>
</ng-template>

<ng-template #conflictCardHeader let-index="index" let-name="name" let-roles="roles">
  <div class="conflict-card-header">
    <div class="conflict-card-kicker">Conflict Path {{ index + 1 }}</div>
    <div class="conflict-identity-row">
      <div class="conflict-identity-key">Identity Name :</div>
      <div class="conflict-identity-value conflict-card-title">{{ name }}</div>
    </div>
    <div class="conflict-identity-row">
      <div class="conflict-identity-key">Role Attributes :</div>
      <ng-container
        *ngTemplateOutlet="roleAttributeList; context: { roles: roles, inline: true, valueClass: 'conflict-identity-value', emptyClass: 'conflict-identity-value conflict-identity-empty' }"
      ></ng-container>
    </div>
  </div>
</ng-template>

<ng-template #reasonPanel let-reason="reason" let-overlap="overlap" let-signals="signals">
  <div class="reason-panel">
    <div class="reason-title">Why this might be happening</div>
    <div class="reason-text">{{ reason }}</div>
    <ng-container *ngTemplateOutlet="overlapWarning; context: { overlap: overlap }"></ng-container>
    <div class="reason-signals">
      @for (signal of signals; track signal) {
        <div class="reason-signal">{{ signal }}</div>
      }
    </div>
  </div>
</ng-template>

<ng-template #overlapWarning let-overlap="overlap">
  @if (hasOverlapDetails(overlap)) {
    <div class="overlap-warning-box">
      <div class="overlap-warning-title">Potential conflict evidence</div>
      <div class="overlap-warning-text">{{ getOverlapMessage(overlap) }}</div>

      <ng-container
        *ngTemplateOutlet="overlapRow; context: { label: 'Hosts', items: getOverlapHosts(overlap) }"
      ></ng-container>
      <ng-container
        *ngTemplateOutlet="overlapRow; context: { label: 'IPs', items: getOverlapIps(overlap) }"
      ></ng-container>
      <ng-container
        *ngTemplateOutlet="overlapRow; context: { label: 'Ports', items: getOverlapPorts(overlap) }"
      ></ng-container>
    </div>
  }
</ng-template>

<ng-template #overlapRow let-label="label" let-items="items">
  @if (items?.length > 0) {
    <div class="overlap-warning-row">
      <div class="overlap-warning-label">{{ label }}</div>
      <ng-container *ngTemplateOutlet="comparisonChipList; context: { items: items }"></ng-container>
    </div>
  }
</ng-template>

<ng-template
  #roleAttributeList
  let-roles="roles"
  let-inline="inline"
  let-valueClass="valueClass"
  let-emptyClass="emptyClass"
>
  @if (roles?.length > 0) {
    <div class="role-attributes" [ngClass]="[inline ? 'inline' : '', valueClass || '']">
      @for (role of roles; track role) {
        <div class="role-attribute-chip">{{ role }}</div>
      }
    </div>
  } @else {
    <div [ngClass]="emptyClass || valueClass || ''">None</div>
  }
</ng-template>

<ng-template #comparisonChipList let-items="items">
  @if (items?.length > 0) {
    <div class="comparison-chip-list">
      @for (item of items; track item) {
        <div class="comparison-chip">{{ item }}</div>
      }
    </div>
  } @else {
    <div>None</div>
  }
</ng-template>
